<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import ReportsStatCards from '@/components/reports/ReportsStatCards.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import IconExport from '@/components/icons/IconExport.vue';
import BarChart from '@/components/ui/BarChart.vue';
import { useReportsAccounts } from '@/composables/useReportsAccounts';
import { formatDate } from '@/utils/formatUtils';
import { downloadCsv } from '@/utils/csvExport';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';

const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string] | null>(() => {
    const range = selectedDateRange.value;
    if (!range) return null;
    return [formatDate(range[0]), formatDate(range[1])];
});

const {
    viewMode,
    activeStatId,
    stats,
    chartData,
    chartOptions,
    dailyAverage,
    loading,
    categoryData,
    rangeIncludesCurrentPeriod,
} = useReportsAccounts(formattedDateRange);

const CATEGORY_LABELS: Record<string, string> = {
    validated: 'Validated',
    'pending-validation': 'Pending Validation',
    delinquent: 'Delinquent',
    'unsettled-accounts': 'Unsettled Accounts',
};

function exportCsv() {
    const cats = categoryData.value;
    const activeId = activeStatId.value;
    const range = formattedDateRange.value;
    if (!range) return;
    const [from, to] = range;
    const dateRangeLabel = `${from} - ${to}`;
    const fileSlug = `${from.replace(/\//g, '-')}_${to.replace(/\//g, '-')}`;
    const fmtNum = (v: number) => v.toLocaleString('en-US');

    if (activeId && cats[activeId]) {
        const label = CATEGORY_LABELS[activeId] ?? activeId;
        const statItem = stats.value.find((s) => s.id === activeId);
        const rows: string[][] = [
            ['Date Range', dateRangeLabel],
            [],
            ['Period', label],
            ...cats[activeId].labels.map((lbl, i) => [lbl, fmtNum(cats[activeId].values[i])]),
            [],
            [`Total ${label}`, String(statItem?.value ?? '')],
            ['Daily Average', dailyAverage.value],
        ];
        downloadCsv(`accounts-${activeId}-${viewMode.value}-${fileSlug}.csv`, rows);
        return;
    }

    const catKeys = ['validated', 'pending-validation', 'delinquent', 'unsettled-accounts'] as const;
    const labels = cats.validated.labels;
    const header = ['Period', ...catKeys.map((k) => CATEGORY_LABELS[k]), 'Daily Total'];
    const dataRows: string[][] = labels.map((lbl, i) => {
        const dayTotal = catKeys.reduce((sum, k) => sum + (cats[k]?.values[i] ?? 0), 0);
        return [lbl, ...catKeys.map((k) => fmtNum(cats[k]?.values[i] ?? 0)), fmtNum(dayTotal)];
    });
    const summaryRows: string[][] = [
        [],
        ...stats.value.map((s) => [s.label, String(s.value)]),
        ['Daily Average', dailyAverage.value],
    ];
    downloadCsv(`accounts-${viewMode.value}-${fileSlug}.csv`, [['Date Range', dateRangeLabel], [], header, ...dataRows, ...summaryRows]);
}
</script>

<template>
    <div class="reports-accounts-tab">
        <div class="reports-tab__toolbar">
            <DateRangePicker
                v-model="selectedDateRange"
                label="Filter by Date:"
                trigger-width="350px"
                trigger-radius="10px"
            />

            <div class="reports-tab__toolbar-right">
                <ToolbarIconButton ariaLabel="Export" @click="exportCsv">
                    <IconExport />
                </ToolbarIconButton>
            </div>
        </div>

        <div class="reports-tab__stats">
            <ReportsStatCards
                :items="stats"
                :active-id="activeStatId"
                :loading="loading"
                @update:active-id="activeStatId = $event"
            />
        </div>

        <div class="reports-accounts-tab__chart">
            <BarChart
                :chart-data="chartData"
                :daily-average="activeStatId ? dailyAverage : null"
                :loading="loading"
                :highlight-current-period="rangeIncludesCurrentPeriod"
                :chart-options="chartOptions"
                :value-formatter="(val: number) => val.toLocaleString()"
                @update:view-mode="viewMode = $event"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.reports-accounts-tab {
    @include tab-base(25px 30px 30px);
}

.reports-tab__toolbar {
    @include tab-toolbar(20px);
    margin-bottom: 25px;
}

.reports-tab__toolbar-right {
    @include tab-toolbar-right;
}

.reports-tab__stats {
    margin-bottom: 20px;
}
</style>
